import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { AlertCircle, ArrowRight, PackageSearch } from 'lucide-react';
import { apiClient } from '@/lib/apiClient';
import { ordersApi } from '@/lib/apiServices';
import { fmt, formatJalaliDate, formatOrderNumber } from '@/lib/format';
import { getCarrier } from '@/lib/carriers';
import { persianDisplayText } from '@/lib/persianUi';
import JalaliDatePicker from '@/components/JalaliDatePicker';
import { PAYMENT_MESSAGES } from '../paymentCopy';
import {
  getChequeStatusLabel,
  ORDER_TRACKING_LABELS,
  getPaymentStatusLabel,
  getSecureStatusLabel,
  getVendorAxisBadgeClass,
  getVendorAxisStatusLabel,
} from '../lib/orderStatus';
import OrderStatusTimeline, { OrderStatusBadge } from './OrderStatusTimeline';
import OrderPaymentButton from './OrderPaymentButton';
import '../orders.css';

const SHIPPED_ITEM_STATUSES = new Set(['handed_to_carrier', 'delivered']);
const TERMINAL_ACCEPTANCE_STATUSES = new Set(['rejected', 'auto_cancelled']);

function getErrorMessage(error) {
  console.error(error);
  return 'خطا در دریافت سفارش.';
}

function formatDate(value) {
  return value ? formatJalaliDate(value, { includeTime: true }) : '—';
}

function groupOrderItems(items = []) {
  const groups = new Map();
  for (const item of items) {
    const key = item.shipment_id ?? 'unassigned';
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(item);
  }
  return [...groups.entries()];
}

function canCancelOrder(order) {
  return (
    order.payment_status === 'unpaid'
    && order.customer_status !== 'cancelled'
    && Number(order.kala_barg_amount) === 0
    && !order.items?.some(
      (item) => SHIPPED_ITEM_STATUSES.has(item.vendor_fulfillment_status),
    )
  );
}

function canConfirmSecureReceipt(order) {
  if (!Array.isArray(order.items) || order.items.length === 0) return true;
  const remainingItems = order.items.filter(
    (item) => !TERMINAL_ACCEPTANCE_STATUSES.has(item.vendor_acceptance_status),
  );
  if (remainingItems.length === 0) return false;
  return remainingItems.every(
    (item) => item.vendor_fulfillment_status === 'delivered' && item.delivered_at,
  );
}

export default function OrderDetail() {
  const { orderId } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(false);
  const [error, setError] = useState('');
  const [cancelError, setCancelError] = useState('');
  const [secureAction, setSecureAction] = useState(null);
  const [secureActionPending, setSecureActionPending] = useState(false);
  const [secureActionError, setSecureActionError] = useState('');
  const [chequeDetails, setChequeDetails] = useState([]);
  const [chequeSubmitting, setChequeSubmitting] = useState(false);
  const [chequeError, setChequeError] = useState('');
  const [chequeNotice, setChequeNotice] = useState('');
  const secureDialogRef = useRef(null);
  const secureTriggerRef = useRef(null);
  const secureStatusHeadingRef = useRef(null);

  useEffect(() => {
    let active = true;
    ordersApi.get(orderId)
      .then(({ data }) => {
        if (!active) return;
        setError('');
        setOrder(data);
        if (data.cheque_status === 'awaiting_registration') {
          let plannedAmounts = [];
          try {
            const plan = window.sessionStorage.getItem(`payment-cheque-plan:${data.id}`);
            const parsedPlan = plan ? JSON.parse(plan) : null;
            plannedAmounts = Array.isArray(parsedPlan?.amounts) ? parsedPlan.amounts : [];
          } catch (storageError) {
            console.error('Unable to read retained cheque amounts:', storageError);
          }
          setChequeDetails(plannedAmounts.map((amount) => ({
            amount: String(amount),
            due_date: '',
            reference: '',
          })));
        }
      })
      .catch((requestError) => {
        if (active) setError(getErrorMessage(requestError));
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [orderId]);

  useEffect(() => {
    if (secureAction && secureDialogRef.current && !secureDialogRef.current.open) {
      secureDialogRef.current.showModal();
    }
  }, [secureAction]);

  const loadOrder = useCallback(async () => {
    try {
      const { data } = await ordersApi.get(orderId);
      setError('');
      setOrder(data);
      return data;
    } catch (requestError) {
      setError(getErrorMessage(requestError));
      return null;
    }
  }, [orderId]);

  const openSecureDialog = (action, event) => {
    secureTriggerRef.current = event.currentTarget;
    setSecureActionError('');
    setSecureAction(action);
  };

  const handleSecureDialogClose = () => {
    setSecureAction(null);
    if (secureTriggerRef.current?.isConnected) {
      secureTriggerRef.current.focus();
    } else {
      secureStatusHeadingRef.current?.focus();
    }
  };

  const handleSecureAction = async () => {
    if (!secureAction) return;
    const action = secureAction;
    setSecureActionPending(true);
    setSecureActionError('');
    const path = action === 'confirm'
      ? '/api/v1/customer/orders/{order_id}/confirm-receipt'
      : '/api/v1/customer/orders/{order_id}/not-received';

    try {
      const { data, error: requestError, response } = await apiClient.POST(
        path,
        { params: { path: { order_id: orderId } } },
      );
      if (requestError || !data) {
        if (response?.status === 409) {
          const detail = requestError?.detail;
          await loadOrder();
          setSecureActionError(
            action === 'confirm'
            && typeof detail === 'string'
            && detail.includes('all remaining items')
              ? PAYMENT_MESSAGES.secureReceiptItemsNotDelivered
              : PAYMENT_MESSAGES.secureOrderChanged,
          );
          secureDialogRef.current?.close();
          return;
        }
        setSecureActionError(PAYMENT_MESSAGES.secureActionError);
        return;
      }

      setOrder(data);
      secureDialogRef.current?.close();
    } catch (requestError) {
      console.error(requestError);
      setSecureActionError(PAYMENT_MESSAGES.secureActionError);
    } finally {
      setSecureActionPending(false);
    }
  };

  const handleCancel = async () => {
    if (!window.confirm('آیا از لغو این سفارش مطمئن هستید؟')) return;
    setCancelling(true);
    setCancelError('');
    try {
      await ordersApi.cancel(orderId);
      await loadOrder();
    } catch (requestError) {
      setCancelError(getErrorMessage(requestError));
    } finally {
      setCancelling(false);
    }
  };

  const submitChequeRegistration = async (event) => {
    event.preventDefault();
    setChequeSubmitting(true);
    setChequeError('');
    setChequeNotice('');
    try {
      const { data, error: requestError, response } = await apiClient.POST(
        '/api/v1/customer/orders/{order_id}/cheques',
        {
          params: { path: { order_id: orderId } },
          body: { cheques: chequeDetails },
        },
      );
      if (requestError || !data) {
        const code = requestError?.code ?? requestError?.detail?.code;
        const detail = requestError?.detail;
        const messages = {
          CHEQUE_REGISTRATION_DOES_NOT_MATCH_PLAN: PAYMENT_MESSAGES.chequeRegistrationMismatchError,
          INVALID_CHEQUE_DETAILS: PAYMENT_MESSAGES.invalidChequeDetailsError,
        };
        setChequeError(
          messages[code]
          ?? (response?.status === 409
            ? PAYMENT_MESSAGES.chequeOrderChanged
            : typeof detail === 'string'
              ? detail
              : PAYMENT_MESSAGES.chequeRegistrationFailed),
        );
      } else {
        setOrder(data);
        setChequeNotice(PAYMENT_MESSAGES.chequeRegistrationComplete);
        try {
          window.sessionStorage.removeItem(`payment-cheque-plan:${data.id}`);
        } catch (storageError) {
          console.error('Unable to clear retained cheque amounts:', storageError);
        }
      }
    } catch (requestError) {
      console.error(requestError);
      setChequeError(PAYMENT_MESSAGES.chequeRegistrationFailed);
    } finally {
      setChequeSubmitting(false);
    }
  };

  if (loading && !order) {
    return <div className="orders-state orders-detail-page" role="status">در حال بارگذاری جزئیات سفارش...</div>;
  }

  if (error && !order) {
    return (
      <section className="orders-detail-page orders-state orders-state--error" role="alert">
        {error}
      </section>
    );
  }

  if (!order) return null;

  const shipmentById = new Map(
    (order.shipments ?? []).map((shipment) => [shipment.id, shipment])
  );
  const itemGroups = groupOrderItems(order.items);
  const cancelAllowed = canCancelOrder(order);

  return (
    <main className="orders-detail-page">
      <Link className="orders-back-link" to="/profile/orders">
        <ArrowRight size={18} aria-hidden="true" /> بازگشت به سفارش‌ها
      </Link>

      {error && <div className="orders-state orders-state--error" role="alert">{error}</div>}
      {cancelError && (
        <div className="orders-state orders-state--error" role="alert">
          <AlertCircle size={18} aria-hidden="true" /> {cancelError}
        </div>
      )}

      <header className="orders-detail-header">
        <div>
          <p className="orders-detail-eyebrow">جزئیات سفارش</p>
          <h1>جزئیات سفارش {formatOrderNumber(order.order_number)}</h1>
          <p>تاریخ ثبت: {formatDate(order.created_at)}</p>
        </div>
        <OrderStatusBadge order={order} role="customer" />
      </header>
      <OrderStatusTimeline order={order} role="customer" />

      {cancelAllowed && (
        <button
          className="orders-cancel-button"
          disabled={cancelling}
          onClick={handleCancel}
          type="button"
        >
          {cancelling ? 'در حال لغو سفارش...' : 'لغو سفارش'}
        </button>
      )}
      <section className="orders-detail-section" aria-label={PAYMENT_MESSAGES.orderPaymentStatusAria}>
        <h2 ref={secureStatusHeadingRef} tabIndex={-1}>
          {getPaymentStatusLabel(order.payment_status)}
        </h2>
        {order.secure_status && (
          <>
            <p className="order-payment-status">
              {getSecureStatusLabel(order.secure_status)}
            </p>
            {order.secure_status === 'awaiting_receipt' && (
              <>
                <p className="order-payment-status">{PAYMENT_MESSAGES.secureHoldingNotice}</p>
                <div className="secure-receipt-actions">
                  {canConfirmSecureReceipt(order) ? (
                    <button
                      className="btn btn-primary"
                      disabled={secureActionPending}
                      onClick={(event) => openSecureDialog('confirm', event)}
                      type="button"
                    >
                      {PAYMENT_MESSAGES.confirmReceiptAction}
                    </button>
                  ) : (
                    <p className="order-payment-status">
                      {PAYMENT_MESSAGES.secureReceiptWaitForDelivery}
                    </p>
                  )}
                  <button
                    className="orders-cancel-button"
                    disabled={secureActionPending}
                    onClick={(event) => openSecureDialog('not_received', event)}
                    type="button"
                  >
                    {PAYMENT_MESSAGES.notReceivedAction}
                  </button>
                </div>
              </>
            )}
            {order.secure_status === 'not_received' && (
              <p className="order-payment-status">
                {PAYMENT_MESSAGES.secureNotReceivedExplanation}
              </p>
            )}
          </>
        )}
        {secureActionError && !secureAction && (
          <div className="orders-state orders-state--error" role="alert">
            {secureActionError}
          </div>
        )}
        {order.cheque_status && (
          <p className="order-payment-status">
            {getChequeStatusLabel(order.cheque_status)}
          </p>
        )}
        {order.cheque_confirmation_deadline_at && (
          <p className="order-payment-status">
            {PAYMENT_MESSAGES.chequeDecisionDeadline} {formatDate(order.cheque_confirmation_deadline_at)}
          </p>
        )}
        {chequeNotice && <p className="order-payment-status" role="status">{chequeNotice}</p>}
        {order.cheque_status === 'awaiting_registration' && (
          <form className="order-cheque-registration" onSubmit={submitChequeRegistration}>
            <h3>{PAYMENT_MESSAGES.chequeAwaitingRegistration}</h3>
            <p>
              {PAYMENT_MESSAGES.chequePrepaymentLabel}: {fmt(order.cheque_prepayment_amount)} تومان
            </p>
            {chequeDetails.length === 0 && (
              <p className="order-payment-status">{PAYMENT_MESSAGES.chequePlanUnavailable}</p>
            )}
            {chequeError && <div className="orders-state orders-state--error" role="alert">{chequeError}</div>}
            {chequeDetails.map((cheque, index) => (
              <fieldset className="order-cheque-row" key={`customer-cheque-${index}`}>
                <legend>{PAYMENT_MESSAGES.chequeCountLabel} · {fmt(index + 1)}</legend>
                <label>
                  {PAYMENT_MESSAGES.chequeAmountLabel}
                  <input
                    inputMode="numeric"
                    min="1"
                    onChange={(event) => setChequeDetails((current) => current.map((item, itemIndex) => (
                      itemIndex === index ? { ...item, amount: event.target.value } : item
                    )))}
                    step="1"
                    required
                    type="number"
                    value={cheque.amount}
                  />
                </label>
                <label>
                  {PAYMENT_MESSAGES.chequeDueDateLabel}
                  <JalaliDatePicker
                    aria-label="تاریخ سررسید چک"
                    onChange={(value) => setChequeDetails((current) => current.map((item, itemIndex) => (
                      itemIndex === index ? { ...item, due_date: value } : item
                    )))}
                    required
                    value={cheque.due_date}
                  />
                </label>
                <label>
                  {PAYMENT_MESSAGES.chequeReferenceLabel}
                  <input
                    maxLength={128}
                    onChange={(event) => setChequeDetails((current) => current.map((item, itemIndex) => (
                      itemIndex === index ? { ...item, reference: event.target.value } : item
                    )))}
                    value={cheque.reference}
                    required
                  />
                </label>
                <button
                  aria-label={`${PAYMENT_MESSAGES.removeCheque} ${fmt(index + 1)}`}
                  className="btn btn-outline"
                  disabled={chequeSubmitting}
                  onClick={() => setChequeDetails((current) => current.filter((_, itemIndex) => itemIndex !== index))}
                  type="button"
                >
                  {PAYMENT_MESSAGES.removeCheque}
                </button>
              </fieldset>
            ))}
            <button
              className="btn btn-outline"
              onClick={() => setChequeDetails((current) => [
                ...current,
                { amount: '', due_date: '', reference: '' },
              ])}
              disabled={chequeSubmitting}
              type="button"
            >
              {PAYMENT_MESSAGES.addCheque}
            </button>
            <button className="btn btn-primary" disabled={chequeSubmitting || chequeDetails.length === 0} type="submit">
              {chequeSubmitting ? PAYMENT_MESSAGES.registeringCheques : PAYMENT_MESSAGES.registerCheques}
            </button>
          </form>
        )}
        {order.payment_status === 'unpaid' && (
          <OrderPaymentButton orderId={order.id} expiresAt={order.expires_at} />
        )}
      </section>

      <section className="orders-detail-section">
        <h2>اقلام و مرسوله‌ها</h2>
        {itemGroups.length > 0 ? (
          itemGroups.map(([shipmentId, items], groupIndex) => {
            const shipment = shipmentId === 'unassigned'
              ? null
              : shipmentById.get(shipmentId);
            const destination = shipment?.dest_address || order.shipping_address;
            const postalCode = shipment?.dest_postal_code || order.shipping_postal_code;

            return (
              <article className="order-shipment-card" key={shipmentId}>
                <header className="order-shipment-header">
                  <h3>{shipment ? `مرسوله ${groupIndex + 1}` : 'اقلام سفارش'}</h3>
                  {destination && <p>{destination}</p>}
                  {postalCode && <p>کد پستی: {postalCode}</p>}
                </header>

                <div className="order-shipment-items">
                  {items.map((item) => {
                    const product = item.product;
                    const carrierCode = item.carrier_code?.trim();
                    const carrier = carrierCode ? getCarrier(carrierCode) : null;
                    const trackingUrl = item.tracking_number
                      ? carrier?.trackUrl(item.tracking_number)
                      : null;
                    return (
                      <div className="order-detail-item" key={item.id}>
                        <div className="order-detail-product">
                          <div className="order-detail-product-image">
                            {product?.image_url ? (
                              <img src={product.image_url} alt={persianDisplayText(product.name_fa, 'محصول')} />
                            ) : (
                              <PackageSearch aria-hidden="true" />
                            )}
                          </div>
                          <div>
                            <h4>{persianDisplayText(product?.name_fa, 'محصول')}</h4>
                            <p>تعداد: {item.quantity}</p>
                            <p>قیمت واحد: {fmt(item.unit_price)} تومان</p>
                          </div>
                        </div>
                        <span className={getVendorAxisBadgeClass(
                          item.vendor_acceptance_status,
                          item.vendor_fulfillment_status,
                        )}>
                          {getVendorAxisStatusLabel(
                            item.vendor_acceptance_status,
                            item.vendor_fulfillment_status,
                          )}
                        </span>
                        <strong>{fmt(Number(item.unit_price) * Number(item.quantity))} تومان</strong>
                        {(item.tracking_number || carrier || item.shipped_at || item.delivered_at) && (
                          <dl className="order-item-tracking">
                            {item.tracking_number && (
                              <div>
                                <dt>{ORDER_TRACKING_LABELS.trackingNumber}</dt>
                                <dd>
                                  {item.tracking_number}
                                  {carrier && <> · {ORDER_TRACKING_LABELS.carrier}: {carrier.label}</>}
                                  {trackingUrl && (
                                    <> <a href={trackingUrl} target="_blank" rel="noopener noreferrer">{ORDER_TRACKING_LABELS.track}</a></>
                                  )}
                                </dd>
                              </div>
                            )}
                            {!item.tracking_number && carrier && (
                              <div><dt>{ORDER_TRACKING_LABELS.carrier}</dt><dd>{carrier.label}</dd></div>
                            )}
                            {item.shipped_at && (
                              <div><dt>تاریخ ارسال</dt><dd>{formatDate(item.shipped_at)}</dd></div>
                            )}
                            {item.delivered_at && (
                              <div><dt>تاریخ تحویل</dt><dd>{formatDate(item.delivered_at)}</dd></div>
                            )}
                          </dl>
                        )}
                      </div>
                    );
                  })}
                </div>
              </article>
            );
          })
        ) : (
          <p className="orders-empty-items">اقلامی برای این سفارش ثبت نشده است.</p>
        )}
      </section>

      <section className="orders-total-card" aria-label="خلاصه مبالغ">
        <div><span>مبلغ محصولات</span><strong>{fmt(order.product_total_amount)} تومان</strong></div>
        <div><span>هزینه ارسال</span><strong>{fmt(order.shipping_cost)} تومان</strong></div>
        <div className="orders-grand-total"><span>مبلغ کل</span><strong>{fmt(order.total_price_amount)} تومان</strong></div>
      </section>

      <dialog
        aria-labelledby="secure-dialog-title"
        aria-describedby="secure-dialog-description"
        className="orders-secure-dialog"
        onCancel={(event) => {
          if (secureActionPending) event.preventDefault();
        }}
        onClose={handleSecureDialogClose}
        ref={secureDialogRef}
      >
        <h2 id="secure-dialog-title">
          {secureAction === 'confirm'
            ? PAYMENT_MESSAGES.confirmReceiptDialogTitle
            : PAYMENT_MESSAGES.notReceivedDialogTitle}
        </h2>
        <p id="secure-dialog-description">
          {secureAction === 'confirm'
            ? PAYMENT_MESSAGES.confirmReceiptDialogBody
            : PAYMENT_MESSAGES.notReceivedDialogBody}
        </p>
        {secureActionError && (
          <p className="orders-state orders-state--error" role="alert">
            {secureActionError}
          </p>
        )}
        <div className="orders-secure-dialog-actions">
          <button
            className="orders-cancel-button"
            autoFocus
            disabled={secureActionPending}
            onClick={() => secureDialogRef.current?.close()}
            type="button"
          >
            {PAYMENT_MESSAGES.secureCancel}
          </button>
          <button
            className="btn btn-primary"
            disabled={secureActionPending}
            onClick={handleSecureAction}
            type="button"
          >
            {secureActionPending
              ? PAYMENT_MESSAGES.secureActionPending
              : secureAction === 'confirm'
                ? PAYMENT_MESSAGES.secureConfirm
                : PAYMENT_MESSAGES.secureReportNotReceived}
          </button>
        </div>
      </dialog>
    </main>
  );
}
